import { graphql } from '@/__generated__';
import { GroupFilter } from '@/__generated__/graphql';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

type Result = ReturnType<typeof useCatalogGroups>;

export type GroupDefinitionListNode = NonNullable<Result['data']>[number];

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetGroupsByAccountId = graphql(/* GraphQL */ `
  query GroupsByAccount($accountId: BigInt!, $first: Int, $sort: GroupSort) {
    catalog(accountId: $accountId) {
      groups(first: $first, sort: $sort) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            name
            uniqueId
            modelCount
            ownerName
            ownerEmail
            ownerSlack
            ownerGithub
          }
        }
      }
    }
  }
`);

type UseGroupsParams = {
  first: number;
  after?: string;
  accountId: number | undefined;
  userId?: number | 'loading' | null;
  filter: GroupFilter | undefined;
  pause?: boolean;
};

export const useCatalogGroups = ({
  first,
  after,
  accountId,
  filter,
  pause,
}: UseGroupsParams) => {
  const query = usePaginatedQuery(
    {
      query: GetGroupsByAccountId,
      variables: {
        first,
        after,
        accountId,
        filter,
      },
      pause: pause || !accountId || !first,
    },
    (data) => {
      const groups = data.catalog.groups;
      const pageInfo = groups?.pageInfo;
      if (groups == null || pageInfo == null) return undefined;
      return {
        totalCount: groups.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.catalog.groups.edges.map((edge) => edge.node);
    },
    () => null,
  );

  return query;
};
